import { useEffect, useMemo, useRef, useState } from "react";
import { useStores } from "../../stores/root-store";
import { observer } from "mobx-react";
import _ from "lodash";
import {
  convertToDate,
  currencyFormatter,
  currentMonthStartDate,
  formatDate,
  getDisplayDeliveryType,
  getOrderDisplayStatus,
  zeroPad,
} from "../../utils/functions";
import { useAuth } from "../../contexts/auth-context";
import { IOrder, IOrderFilters } from "../../stores/order";
import { OrderStatus, PaymentStatus } from "../../stores/index.d";
import Paginate from "../../shared/pagination/paginate";
import OrdersFilter from "./orders-filter";
import OrderDetails from "../orders/order-details";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";

const cols: string[] = [
  "#",
  "Order #",
  "Order Date",
  "Type",
  "Amount",
  "Status",
  // "Customer",
  "",
];

const OrdersView = observer(() => {
  const { isAuthenticated, isShopAdmin, isShopUser } = useAuth();
  const { orderStore } = useStores();
  const { orders, fetchOrders } = orderStore;
  const [viewOrder, setViewOrder] = useState<IOrder>();
  const [ordersPaginated, setOrdersPaginated] = useState<IOrder[]>([]);
  const [orderFilters, setOrderFilters] = useState<IOrderFilters>({
    orderType: 0,
    startDate: currentMonthStartDate(),
    endDate: new Date(),
    orderStatus: OrderStatus.Completed,
  });
  const [pageNumber, setPageNumber] = useState<number>(1);
  const tableRef = useRef<HTMLTableElement>(null);

  const dt = getDisplayDeliveryType(orderFilters.orderType).text;
  const st = getOrderDisplayStatus(orderFilters.orderStatus).text;
  const pageSize = 25;

  useEffect(() => {
    if (isAuthenticated && (isShopAdmin || isShopUser)) fetchOrders();
  }, [isAuthenticated, isShopAdmin, isShopUser]);

  const orderList = useMemo(() => {
    setPageNumber(0);
    return orders.filter(
      (o: IOrder) =>
        (!orderFilters.startDate ||
          convertToDate(o.dateUpdated) >= orderFilters.startDate) &&
        (!orderFilters.endDate ||
          convertToDate(o.dateUpdated) <= orderFilters.endDate) &&
        o.paymentStatus == PaymentStatus.Paid &&
        o.orderStatus != OrderStatus.Cancelled &&
        (!orderFilters.orderType ||
          orderFilters.orderType == o.orderDeliveryType) &&
        (!orderFilters.orderStatus || orderFilters.orderStatus == o.orderStatus)
    );
  }, [orders, orders?.length, orderFilters]);

  const handleViewOrder = (order?: IOrder) => {
    setViewOrder(order);
  };

  const exportTableToExcel = () => {
    const t = tableRef.current;
    if (!t) return;

    const table = t.cloneNode(true) as HTMLTableElement;

    const rows = table.rows;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.cells.length > 0) row.deleteCell(row.cells.length - 1);
    }

    const period = `${formatDate(
      orderFilters.startDate,
      "DD MMM yy"
    )}-${formatDate(orderFilters.endDate, "DD MMM yy")}`;

    const headerRow = table.insertRow(0);
    headerRow.insertCell(0).textContent = "Report period: " + period;

    const newRow1 = table.insertRow(1);
    newRow1.insertCell(0).textContent = "Total number of orders";
    newRow1.insertCell(1).textContent = orderList.length.toString();
    newRow1.insertCell(2).textContent = "";
    newRow1.insertCell(3).textContent = "Order Type";
    newRow1.insertCell(4).textContent = dt;

    const newRow2 = table.insertRow(2);
    newRow2.insertCell(0).textContent = "Total amount";
    newRow2.insertCell(1).textContent = currencyFormatter(
      orderList.reduce((acc, curr: IOrder) => acc + curr.orderTotal, 0)
    );
    newRow2.insertCell(2).textContent = "";
    newRow2.insertCell(3).textContent = "Order Status";
    newRow2.insertCell(4).textContent = st;

    table.insertRow(3);

    const ws = XLSX.utils.table_to_sheet(table);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    XLSX.writeFile(wb, `${period}.xlsx`);
  };

  return (
    <>
      <div className="row mb-2">
        <div className="mb-4">
          <h6 className="mb-3">
            Report Period: {formatDate(orderFilters.startDate, "DD MMM yy")} -{" "}
            {formatDate(orderFilters.endDate, "DD MMM yy")}
            <a
              className="float-end"
              href="#"
              title="Export data"
              onClick={exportTableToExcel}
            >
              <FontAwesomeIcon icon={faFileExcel} className="fs-2" />
            </a>
          </h6>
          <div className="row">
            <div className="col">
              <div className="">
                Total number of orders:{" "}
                <span className="badge bg-primary fs-6">
                  {orderList.length}
                </span>
              </div>
              <div className="mt-1">
                Total amount:{" "}
                <span className="badge bg-primary fs-6">
                  {currencyFormatter(
                    orderList.reduce(
                      (acc, curr: IOrder) => acc + curr.orderTotal,
                      0
                    )
                  )}
                </span>
              </div>
            </div>
            <div className="col">
              <div className="">
                Order Type:{" "}
                <span className="text-success">{dt == "NA" ? "All" : dt}</span>
              </div>
              <div className="mt-1">
                Order Status:{" "}
                <span className="text-success">{st == "NA" ? "All" : st}</span>
              </div>
            </div>
          </div>
        </div>
        <OrdersFilter filters={orderFilters} setFilters={setOrderFilters} />
      </div>

      <table
        className="table table-responsive table-striped shadow-lg"
        id="orderDataTable"
        ref={tableRef}
      >
        <thead>
          <tr>
            {cols.map((c, i) => (
              <th
                scope="col"
                className="border-top-0 border-start-0 border-end-0 border-3 border-warning"
                key={i}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ordersPaginated.map((order: IOrder, i) => {
            const st = getOrderDisplayStatus(order.orderStatus);
            const del = getDisplayDeliveryType(order.orderDeliveryType);

            return (
              <tr key={order.orderId}>
                <th scope="row">
                  {(pageNumber > 0 ? pageSize * pageNumber + i : i) + 1}
                </th>
                <td>{zeroPad(order.orderId, 8)}</td>
                <td>{formatDate(order.dateUpdated ?? order.orderDate)}</td>
                <td className={del.cssClass}>{del.text}</td>
                <td>{currencyFormatter(order.orderTotal)}</td>
                <td className={st.cssClass}>{st.text}</td>
                {/* <td>-</td> */}
                <td>
                  <button
                    className="btn btn-secondary m-0 ms-2"
                    type="button"
                    onClick={() => handleViewOrder(order)}
                  >
                    View
                  </button>
                </td>
              </tr>
            );
          })}
          {!ordersPaginated?.length && (
            <tr>
              <th colSpan={8} className="text-center">
                No data available
              </th>
            </tr>
          )}
        </tbody>
      </table>
      <Paginate
        itemsPerPage={pageSize}
        items={orderList}
        resetPage={pageNumber === 0}
        onPaginate={(orders: IOrder[], pageNo: number) => {
          setPageNumber(pageNo);
          setOrdersPaginated(orders);
        }}
      />
      {ordersPaginated.length > 0 && viewOrder && (
        <OrderDetails
          order={viewOrder}
          show={viewOrder != null}
          setShowModal={() => handleViewOrder(undefined)}
        />
      )}
    </>
  );
});

export default OrdersView;
